<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useAppStore } from '../stores/app';
import { projects, projectCategories } from '../data/portfolio';
import type { ProjectCategory } from '../types';

const appStore = useAppStore();
const activeFilter = ref<ProjectCategory>('all');
const isVisible = ref(false);
const visibleProjects = ref<Set<string>>(new Set());

const filteredProjects = computed(() => {
  if (activeFilter.value === 'all') {
    return projects;
  }
  return projects.filter(p => p.category === activeFilter.value);
});

function setFilter(filter: ProjectCategory) {
  activeFilter.value = filter;
  visibleProjects.value = new Set();
  setTimeout(() => {
    animateProjects();
  }, 100);
}

function animateProjects() {
  filteredProjects.value.forEach((project, index) => {
    setTimeout(() => {
      visibleProjects.value.add(project.id);
    }, index * 80);
  });
}

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          animateProjects();
          observer.disconnect();
        }
      });
    },
    { threshold: 0.1 }
  );

  const section = document.getElementById('projects');
  if (section) {
    observer.observe(section);
  }
});

function getCategoryName(catId: string) {
  const cat = projectCategories.find(c => c.id === catId);
  return cat ? (appStore.isZh ? cat.name : cat.nameEn) : catId;
}
</script>

<template>
  <section id="projects" class="projects-section section">
    <div class="container">
      <h2 class="section-title">
        <i class="fas fa-briefcase"></i>
        {{ appStore.isZh ? '作品集' : 'Projects' }}
      </h2>
      <div class="section-divider"></div>
      <p class="section-subtitle">
        {{ appStore.isZh 
          ? '吾之代码如法宝，各有神通。或如混天绫般灵动，或似火尖枪般刚猛' 
          : 'My code is like magical treasures, each with its own power' }}
      </p>

      <div class="filter-tabs">
        <button
          v-for="cat in projectCategories"
          :key="cat.id"
          class="filter-tab"
          :class="{ active: activeFilter === cat.id }"
          @click="setFilter(cat.id as ProjectCategory)"
        >
          <i :class="['fas', cat.icon]"></i>
          <span>{{ appStore.isZh ? cat.name : cat.nameEn }}</span>
        </button>
      </div>

      <div class="projects-grid">
        <div
          v-for="project in filteredProjects"
          :key="project.id"
          class="project-card"
          :class="{ 
            visible: visibleProjects.has(project.id),
            featured: project.featured 
          }"
        >
          <div class="project-image">
            <img :src="project.image" :alt="project.title" />
            <div class="project-overlay">
              <div class="project-links">
                <a 
                  v-if="project.githubUrl" 
                  :href="project.githubUrl" 
                  target="_blank"
                  rel="noopener noreferrer"
                  class="project-link"
                >
                  <i class="fab fa-github"></i>
                </a>
                <a 
                  v-if="project.liveUrl" 
                  :href="project.liveUrl" 
                  target="_blank"
                  rel="noopener noreferrer"
                  class="project-link"
                >
                  <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
            </div>
            <div v-if="project.featured" class="featured-badge">
              <i class="fas fa-crown"></i>
              {{ appStore.isZh ? '精选' : 'Featured' }}
            </div>
          </div>
          
          <div class="project-content">
            <div class="project-category">
              <i class="fas fa-tag"></i>
              {{ getCategoryName(project.category) }}
            </div>
            <h3 class="project-title">
              {{ appStore.isZh ? project.title : project.titleEn }}
            </h3>
            <p class="project-description">
              {{ appStore.isZh ? project.description : project.descriptionEn }}
            </p>
            <div class="project-tags">
              <span v-for="tag in project.tags" :key="tag" class="project-tag">
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="view-more">
        <a 
          href="https://github.com/ctkqiang" 
          target="_blank"
          rel="noopener noreferrer"
          class="nezha-btn nezha-btn-outline"
        >
          <i class="fab fa-github"></i>
          {{ appStore.isZh ? '查看更多项目' : 'View More on GitHub' }}
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped>
@import '../styles/components/projects.css';
</style>
